import { ArrowLeft, Play, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.tsx';
import { ApiError } from '../../lib/api.ts';
import type { Club } from '../../lib/equipment.ts';
import { listClubs } from '../../lib/equipment.ts';
import type { Friend } from '../../lib/friends.ts';
import { listFriends } from '../../lib/friends.ts';
import { createGame } from '../../lib/putting.ts';

/** Formats proposés. Neuf trous par défaut : c'est ce que dure une session de
 *  putting avant que l'attention ne parte, et cela tient dans une pause. */
const FORMATS = [6, 9, 12, 18];

export function NewPuttingGame() {
    const navigate = useNavigate();
    /* Contexte de séance, quand on arrive depuis le déroulé d'un plan.
     *
     * Dans l'URL plutôt que dans l'état du routeur : au practice, un écran se
     * verrouille et l'application se recharge sans prévenir. Une adresse
     * survit à ça, un état en mémoire non — et l'étape serait restée ouverte
     * pour toujours. */
    const [params] = useSearchParams();
    const runId = params.get('run');
    const runPosition = params.get('pos') ? Number(params.get('pos')) : null;
    const [friends, setFriends] = useState<Friend[]>([]);
    const [putters, setPutters] = useState<Club[]>([]);
    /* Le partenaire amené depuis son profil est déjà coché : c'est l'intention
     * qu'on avait en cliquant « Un putting » sur sa fiche. Il reste
     * décochable, ce n'est pas une contrainte. */
    const [selected, setSelected] = useState<Set<string>>(() => {
        const v = params.get('inviter');
        return new Set(v ? [v] : []);
    });
    const [holeCount, setHoleCount] = useState(9);
    const [name, setName] = useState('');
    const [clubId, setClubId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        void Promise.all([listFriends(), listClubs()])
            .then(([f, c]) => {
                setFriends(f);
                const p = c.filter((x) => x.category === 'putter');
                setPutters(p);
                // Un seul putter : le choix n'en est pas un, on le prend.
                if (p.length === 1) setClubId(p[0]!.id);
            })
            .catch(() => setError('Chargement impossible'));
    }, []);

    const toggle = (id: string): void => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const start = async (): Promise<void> => {
        setBusy(true); setError(null);
        try {
            const state = await createGame({
                name: name.trim() || null,
                holeCount,
                friendIds: [...selected],
                clubId,
                // Passés par l'URL quand on arrive depuis le déroulé d'une
                // séance : c'est ce qui fera cocher l'étape une fois la partie
                // terminée. Le serveur refuse une séance qui n'est pas la vôtre,
                // donc rien ne se joue sur la confiance faite à ces paramètres.
                runId, runPosition,
            });
            void navigate(`/putting/${state.game.id}`);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Création impossible');
            setBusy(false);
        }
    };

    return (
        <div className="space-y-5 py-2">
            <header>
                <Link to="/putting" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Putting
                </Link>
                <h1 className="text-2xl font-bold tracking-tight">Nouvelle partie</h1>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="mb-2 text-sm font-semibold">Format</h2>
                <div className="grid grid-cols-4 gap-2">
                    {FORMATS.map((n) => (
                        <button key={n} type="button" onClick={() => setHoleCount(n)}
                            aria-pressed={holeCount === n}
                            className={`h-12 rounded-field font-bold tabular-nums transition-colors ${
                                holeCount === n ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                            }`}>
                            {n}
                        </button>
                    ))}
                </div>
                {/* Dire pourquoi le nombre est figé évite la question « et si on
                    veut continuer ? » au dixième trou. */}
                <p className="mt-2 text-xs opacity-60">
                    Trous. Fixé au départ : sans arrivée commune, deux scores ne se
                    comparent pas.
                </p>
            </section>

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="mb-2 text-sm font-semibold">Votre putter</h2>
                {putters.length === 0 ? (
                    <div className="text-sm opacity-70">
                        Aucun putter dans votre matériel. Vous pouvez jouer quand même,
                        mais la partie ne comptera pas dans vos statistiques de putter.
                        <Link to="/materiel" className="btn btn-ghost btn-sm mt-2 gap-2">
                            Ajouter un putter
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {putters.map((p) => (
                            <button key={p.id} type="button" onClick={() => setClubId(p.id)}
                                aria-pressed={clubId === p.id}
                                className={`flex w-full items-center gap-3 rounded-field p-3 text-left transition-colors ${
                                    clubId === p.id ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                                }`}>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-semibold">{p.label}</span>
                                    {(p.brand ?? p.model) && (
                                        <span className="block truncate text-xs opacity-70">
                                            {[p.brand, p.model].filter(Boolean).join(' ')}
                                        </span>
                                    )}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="mb-1 text-sm font-semibold">Inviter</h2>
                <p className="mb-3 text-xs opacity-60">
                    Ils recevront l'invitation dans l'application. Vous pouvez aussi
                    partir seul et les inviter en cours de route.
                </p>

                {friends.length === 0 ? (
                    <Link to="/amis" className="btn btn-ghost btn-sm gap-2">
                        <UserPlus size={16} /> Ajouter un partenaire
                    </Link>
                ) : (
                    <div className="space-y-2">
                        {friends.map((f) => (
                            <button key={f.id} type="button" onClick={() => toggle(f.id)}
                                aria-pressed={selected.has(f.id)}
                                className={`flex w-full items-center gap-3 rounded-field p-2 text-left transition-colors ${
                                    selected.has(f.id) ? 'bg-primary text-primary-content' : 'bg-base-100 hover:bg-base-300'
                                }`}>
                                {/* 40 et non 36 : en dessous, la pastille
                                    d'index ne se lit plus dehors. */}
                                <Avatar name={f.displayName} email={f.email}
                                    avatarUrl={f.avatarUrl} size={40}
                                    handicap={f.handicapIndex} />
                                <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                                    {f.displayName}
                                </span>
                                <input type="checkbox" readOnly checked={selected.has(f.id)}
                                    className="checkbox checkbox-sm pointer-events-none" />
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section className="rounded-box bg-base-200 p-4">
                <label className="block">
                    <span className="mb-1 block text-sm font-semibold">Nom de la partie</span>
                    <input
                        type="text"
                        maxLength={80}
                        placeholder="Facultatif — « Jeudi soir », « Revanche »"
                        className="input input-bordered w-full"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </label>
            </section>

            <button type="button" disabled={busy} onClick={() => void start()}
                className="btn btn-primary btn-block h-14 gap-2 text-lg font-bold tap-target">
                <Play size={20} /> Lancer la partie
            </button>
        </div>
    );
}
